import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DiscountCode } from '~app/modules/checkout/models';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { CartUpdateApplicationEvent } from '~shared/modules/application-event/models/cart-update-application-event.model';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { ICart, ICartSuccess } from '~shared/modules/cart/models';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { TrackingData } from '~shared/modules/shopping-cart-global/models/add-to-cart.model';
import { CartFeatureService } from '~shared/modules/shopping-cart-global/services/cart-feature.service';
import { GetCartTotalQuantity } from '~shared/modules/shopping-cart-global/store/cart/cart.actions';
import { CartOfferLine } from '~shared/modules/shopping-cart-store';
import { CartState } from '~shared/modules/shopping-cart-store/models/cart-state.model';
import { ShoppingCartError } from '../classes/shopping-cart-error.class';
import { CART_SERVICE_INSTANCE_KEY, DEFAULT_CONFIG } from '../constants/cart-service.constants';
import { ICartMerge } from '../models';
import { ICartServiceState } from '../models/cart-service-state.model';
import { ICartService } from '../models/cart-service.model';
import { IOfferLine } from '../models/offer-line.model';
import { ISellerLine } from '../models/seller-line.model';
import { LocalCartService } from './local-cart.service';
import { RemoteCartService } from './remote-cart.service';
import { ICartMini } from '../models/shopping-cart-mini.model';

@Injectable({
  providedIn: 'root',
})
export class CartService implements ICartService, OnDestroy {
  public onChange$: Subject<boolean>;
  public onCartMerge$: Subject<boolean>;
  public cartMerge: ICartMerge;

  private static get serviceState(): ICartServiceState {
    return window[CART_SERVICE_INSTANCE_KEY];
  }

  private static set serviceState(state: ICartServiceState) {
    window[CART_SERVICE_INSTANCE_KEY] = state;
  }

  private get currentCartService(): LocalCartService | RemoteCartService {
    return CartService.serviceState.isCartModeForRegisteredUser ? this.remoteCartService : this.localCartService;
  }

  private readonly unsubscribe$ = new Subject<void>();

  private static mapOfferLineError(item: IOfferLine): IOfferLine {
    const { invalidParams, ...rest } = item;

    if (invalidParams?.length) {
      const errorInvalidParams = invalidParams.reduce(
        (acc, { name, ...restParam }) => ({ ...acc, [name]: restParam }),
        {}
      );

      return {
        ...rest,
        invalidParams,
        error: new ShoppingCartError('Validation errors', errorInvalidParams),
      };
    }

    return item;
  }

  constructor(
    private readonly identityService: IdentityService,
    private readonly localCartService: LocalCartService,
    private readonly cartFeatureService: CartFeatureService,
    private readonly remoteCartService: RemoteCartService,
    private readonly applicationEventService: ApplicationEventService,
    private readonly store$: Store<CartState>
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.onChange$.complete();
    this.onCartMerge$.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    CartService.serviceState.instanceCount -= 1;
    if (CartService.serviceState.instanceCount) {
      CartService.serviceState.instanceCount = null;
    }
  }

  getRestructuredCartItem(sellerLine: ISellerLine) {
    return {
      seller: sellerLine.seller,
      items: sellerLine.items.map(CartService.mapOfferLineError),
      sellerTotals: sellerLine.sellerTotals,
      maxShippingTimeTotal: sellerLine.maxShippingTimeTotal,
      minShippingTimeTotal: sellerLine.minShippingTimeTotal,
    };
  }

  read(accountType: PriceTypes): Observable<ICart | ShoppingCartError> {
    const readCart$ = this.currentCartService.read(accountType).pipe(
      // Transform invalidParams into ShoppingCartError and save it in product line's 'error' key
      map((shoppingCart: ICart) => {
        const items = shoppingCart.items.map((sellerLine: ISellerLine) => this.getRestructuredCartItem(sellerLine));
        return { ...shoppingCart, items };
      })
    );

    return this.launch(readCart$);
  }

  readWithDiscount(discountCode: string): Observable<DiscountCode | ShoppingCartError> {
    const readCart$ = this.remoteCartService.readWithDiscount(discountCode).pipe(
      map((discountCodeResponse: DiscountCode) => {
        const items = discountCodeResponse.cart.items.map((sellerLine: ISellerLine) => {
          sellerLine.items = sellerLine.items.map(CartService.mapOfferLineError);
          return this.getRestructuredCartItem(sellerLine);
        });
        return { ...discountCodeResponse, items };
      })
    );

    return this.launch(readCart$);
  }

  addProduct(
    offer: string,
    productId: string,
    quantity: number,
    valueAddedServices: { offer: string; quantity: number; selected: boolean; type: string }[],
    trackingData: TrackingData,
    accountType: PriceTypes
  ): Observable<IOfferLine | ShoppingCartError> {
    const addProduct$ = this.currentCartService
      .addProduct(offer, productId, quantity, valueAddedServices, trackingData, accountType)
      .pipe(
        tap(() => {
          this.onChange$.next(false);
        }),
        map((productLine: IOfferLine): IOfferLine => {
          if (productLine.invalidParams && productLine.invalidParams?.length) {
            const { name, ...rest } = productLine.invalidParams[0];

            throw new ShoppingCartError('Validation error', {
              [`${name}`]: rest,
            });
          }

          return productLine;
        })
      );

    return this.launch(addProduct$);
  }

  updateQuantity(
    cartLine: CartOfferLine,
    quantity: number,
    accountType: PriceTypes
  ): Observable<IOfferLine | ShoppingCartError> {
    const updateQuantity$ = this.currentCartService.updateQuantity(cartLine, quantity, accountType).pipe(
      tap(() => {
        this.onChange$.next(false);
      }),
      map((res: IOfferLine | ShoppingCartError) => {
        return res instanceof ShoppingCartError ? res : CartService.mapOfferLineError(res);
      })
    );

    return this.launch(updateQuantity$);
  }

  deleteProduct(
    offer: string,
    cartLineId: string,
    hasToStoreNotification = true
  ): Observable<ICartSuccess | ShoppingCartError> {
    const deleteProduct$ = this.currentCartService.deleteProduct(offer, cartLineId, hasToStoreNotification).pipe(
      tap(() => {
        this.onChange$.next(false);
      })
    );

    return this.launch(deleteProduct$);
  }

  deleteValueAddedService(payload: { vasId: string; offerId: string }) {
    const deleteValueAddedService$ = this.currentCartService
      .deleteValueAddedService(payload.vasId, payload?.offerId)
      .pipe(
        tap(() => {
          this.onChange$.next(false);
        })
      );

    return this.launch(deleteValueAddedService$);
  }

  checkout(force = false): Observable<ICart | ICartMini | ShoppingCartError> {
    return this.identityService.isAuthorized$.pipe(
      take(1),
      mergeMap((isAuthorized: boolean) => {
        if (!isAuthorized) {
          const errorMessage = 'Checkout is available for registered user only';
          return throwError(
            new ShoppingCartError(errorMessage, {
              common: { reason: errorMessage },
            })
          );
        }

        const checkout$ = this.currentCartService.checkout(force).pipe(
          tap(() => {
            this.onChange$.next(false);
          })
        );

        return this.launch(checkout$);
      })
    );
  }

  getCartTotalQuantity(): Observable<number | null> {
    return this.currentCartService.getItemsInCartCount();
  }

  private initialize(): void {
    if (!CartService.serviceState) {
      this.cartMerge = null;
      CartService.serviceState = {
        instanceCount: 1,
        isCartModeChangeInProgress$: new BehaviorSubject<boolean>(false),
        isCartModeForRegisteredUser: undefined,
        onCartChange$: new Subject(),
        onCartMerge$: new Subject(),
      };
    } else {
      CartService.serviceState.instanceCount += 1;
    }

    this.onChange$ = CartService.serviceState.onCartChange$;
    this.onCartMerge$ = CartService.serviceState.onCartMerge$;

    this.identityService.isAuthorized$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((isAuthorized: boolean) => isAuthorized !== CartService.serviceState.isCartModeForRegisteredUser)
      )
      .subscribe((isUserRegistered: boolean) => {
        const isChangeInProgress = CartService.serviceState.isCartModeChangeInProgress$.getValue();
        if (!isChangeInProgress) {
          this.changeCartMode(isUserRegistered);
        }
      });
  }

  private changeCartMode(isUserRegistered: boolean): void {
    CartService.serviceState.isCartModeChangeInProgress$.next(true);
    CartService.serviceState.isCartModeForRegisteredUser = isUserRegistered;

    if (!isUserRegistered) {
      CartService.serviceState.isCartModeChangeInProgress$.next(false);
      this.onChange$.next(true);
      return;
    }

    this.mergeLocalCartWithRemote();
  }

  private mergeLocalCartWithRemote(): void {
    const items = this.localCartService.getCart().map((productLine) => ({
      offer: productLine.offer,
      quantity: productLine.quantity,
      trackingData: productLine.trackingData,
      valueAddedServices: productLine.valueAddedServices?.map((service) => ({
        offer: service.id,
        quantity: service.quantity,
        type: service?.type,
        selected: service?.selected,
      })),
    }));

    if (!items.length) {
      this.store$.dispatch(new GetCartTotalQuantity());
      CartService.serviceState.isCartModeChangeInProgress$.next(false);
      this.onChange$.next(true);
      return;
    }

    this.remoteCartService
      .merge(items)
      .pipe(
        take(1),
        tap((mergeCartResponse: ICartMerge) => {
          this.cartMerge = { ...mergeCartResponse };
          this.onCartMerge$.next(mergeCartResponse.merged);
          localStorage.removeItem(DEFAULT_CONFIG.cartLocalStorageName);
          this.store$.dispatch(new GetCartTotalQuantity());
        }),
        switchMap(() => this.cartFeatureService.isCartUpdateFeatureEnabled),
        tap((isCartUpdateFFEnabled) => {
          if (isCartUpdateFFEnabled) {
            this.remoteCartService
              .read()
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((offerLine: ICart) => {
                const cartOfferLine = offerLine.items.map((productLine) => {
                  return productLine.items;
                });

                const offers = _.flattenDepth(cartOfferLine, 1);
                this.applicationEventService.publish(new CartUpdateApplicationEvent(offers as CartOfferLine[], false));
              });
          }
        }),
        catchError(() => {
          return of(null);
        })
      )
      .subscribe(() => {
        this.onChange$.next(true);
        CartService.serviceState.isCartModeChangeInProgress$.next(false);
      });
  }

  private launch(request: Observable<any>): Observable<any> {
    return CartService.serviceState.isCartModeChangeInProgress$.pipe(
      filter((isCartModeChangeInProgress: boolean) => !isCartModeChangeInProgress),
      mergeMap(() => request)
    );
  }
}
